/**
 * API hooks for email override operations.
 *
 * Per-lead email overrides allow users to customize individual emails
 * in a sequence for specific contacts.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { baseAPICall } from 'services/baseAPICall'
import { clientInstance } from 'utils/http-client'

// Types
export interface EmailOverride {
  id: number
  lead_id: number
  lead_email: string
  step_id: number
  custom_subject: string
  custom_body: string
  created_at: string
  updated_at: string
}

export interface SaveEmailOverridePayload {
  lead_id: number
  step_id: number
  custom_subject: string
  custom_body: string
}

export interface BulkSaveEmailOverridesPayload {
  overrides: SaveEmailOverridePayload[]
}

export interface ApplyOverridesResult {
  overrides_applied: number
  leads_updated: number
}

// Query keys
export const emailOverrideKeys = {
  all: ['email-overrides'] as const,
  byCampaign: (bisonCampaignId: number) =>
    [...emailOverrideKeys.all, 'campaign', bisonCampaignId] as const,
}

// API calls
const getEmailOverrides = async (
  bisonCampaignId: number
): Promise<EmailOverride[]> => {
  return baseAPICall<EmailOverride[]>(() =>
    clientInstance.get(
      `/emailbison/campaigns/${bisonCampaignId}/email-overrides/`
    )
  )
}

const saveEmailOverrides = async (
  bisonCampaignId: number,
  payload: BulkSaveEmailOverridesPayload
): Promise<EmailOverride[]> => {
  return baseAPICall<EmailOverride[]>(() =>
    clientInstance.post(
      `/emailbison/campaigns/${bisonCampaignId}/email-overrides/`,
      payload
    )
  )
}

const deleteEmailOverride = async (
  bisonCampaignId: number,
  overrideId: number
): Promise<void> => {
  return baseAPICall<void>(() =>
    clientInstance.delete(
      `/emailbison/campaigns/${bisonCampaignId}/email-overrides/${overrideId}/`
    )
  )
}

const applyEmailOverrides = async (
  bisonCampaignId: number
): Promise<ApplyOverridesResult> => {
  return baseAPICall<ApplyOverridesResult>(() =>
    clientInstance.post(
      `/emailbison/campaigns/${bisonCampaignId}/email-overrides/apply/`
    )
  )
}

// Hooks
export const useGetEmailOverrides = (
  bisonCampaignId: number,
  enabled = true
) => {
  return useQuery({
    queryKey: emailOverrideKeys.byCampaign(bisonCampaignId),
    queryFn: () => getEmailOverrides(bisonCampaignId),
    enabled: enabled && bisonCampaignId > 0,
  })
}

export const useSaveEmailOverrides = (bisonCampaignId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: BulkSaveEmailOverridesPayload) =>
      saveEmailOverrides(bisonCampaignId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: emailOverrideKeys.byCampaign(bisonCampaignId),
      })
    },
  })
}

export const useDeleteEmailOverride = (bisonCampaignId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (overrideId: number) =>
      deleteEmailOverride(bisonCampaignId, overrideId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: emailOverrideKeys.byCampaign(bisonCampaignId),
      })
    },
  })
}

export const useApplyEmailOverrides = (bisonCampaignId: number) => {
  return useMutation({
    mutationFn: () => applyEmailOverrides(bisonCampaignId),
  })
}
